import React from "react";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

/* A candidate's email and phone are checked the way Analyse Resume checks them — same rules,
   same messages — wherever a recruiter types one in, so a value the resume screen would refuse
   can't be saved from sourcing either. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const INVALID_EMAIL_MESSAGE = "Please enter a valid email ID";
export const INVALID_PHONE_MESSAGE = "Please enter a valid phone number with country code";

export const isValidEmail = (value) => EMAIL_PATTERN.test((value || "").trim());

// The input only ever hands back an E.164 string (or undefined), so this is a real number with
// its country code or nothing.
export const isValidPhone = (value) => !!value && isValidPhoneNumber(value);

/** A stored number as the E.164 value the phone input expects. Anything that can't be read
 *  as one — no country code, or not a number at all — opens the editor empty rather than
 *  seeding it with text the input would refuse. */
export const toE164 = (value) => {
    try {
        return parsePhoneNumber(value || "")?.number || "";
    } catch {
        return "";
    }
};

// The country follows the dialling code typed, as on Analyse Resume, so the picker only
// shows the flag rather than offering a second way to set it. The flag needs its country's
// name as a label (it's the icon's title); before a code is typed there is no country, and
// the library's own option for that state is labelled "International".
const FlagOnly = ({ value, options, iconComponent: Icon }) => {
    const label = options?.find((o) => o.value === value)?.label || "International";
    return (
        <div className="pointer-events-none flex items-center mr-1.5">
            {Icon && <Icon country={value} label={label} />}
        </div>
    );
};

/**
 * International phone field: starts at "+", so the number can't be entered without its
 * country code. `className` styles the outer box; the inner input is reset to sit flush in it.
 *
 * The reset is scoped and !important because other screens inject global .PhoneInputInput
 * rules (44px height, 1rem text) that would otherwise blow this up inside a compact row.
 */
export const PhoneNumberInput = ({ value, onChange, className = "", ...inputProps }) => (
    <>
        <style>{`
            .sourcing-phone-input.PhoneInput .PhoneInputInput {
                height: auto !important;
                padding: 0 !important;
                border: 0 !important;
                border-radius: 0 !important;
                background: transparent !important;
                font-size: inherit !important;
                color: inherit !important;
                outline: none !important;
                min-width: 0;
            }
            .sourcing-phone-input.PhoneInput .PhoneInputCountry { margin-right: 0 !important; }
        `}</style>
        <PhoneInput
            international
            countryCallingCodeEditable
            smartCaret
            value={value || undefined}
            onChange={(next) => onChange(next || "")}
            countrySelectComponent={FlagOnly}
            className={`sourcing-phone-input flex items-center ${className}`}
            {...inputProps}
        />
    </>
);
